import { TimelineSceneBlock } from '../../../models';
import { attributesMatch } from '../../../utils/attributes-match';

export interface CyElement {
	group: 'nodes' | 'edges';
	data: Record<string, unknown>;
	classes?: string;
}

export type GraphNodeKind = 'scene' | 'chapter';

export interface GraphNodeRef {
	slug: string;
	disabled?: boolean;
	attributes?: Record<string, unknown>;
	/** Scene-only — safe to ignore for chapters. */
	externalChapter?: string;
	/** Chapter-only — label hint (total scenes in the chapter). */
	scenesCount?: number;
}

interface PendingBranchEnd {
	nodeId: string;
	attrKey: string;
	attrValue: string;
	isOnPath: boolean;
	isOffPath: boolean;
}

/**
 * Build Cytoscape elements from a list of graph node refs.
 *
 * Polymorphic over `kind`: the same fork/merge branching logic is applied to
 * scenes and chapters — they both carry `attributes` and `disabled` in the
 * same shape. `kind` drives node class + label layout only.
 */
export function buildCytoscapeElements(
	refs: GraphNodeRef[],
	allBlocks: TimelineSceneBlock[],
	activeSlug: string | null,
	argsMap: Record<string, string | number | boolean>,
	kind: GraphNodeKind = 'scene',
): CyElement[] {
	const elements: CyElement[] = [];
	let prevNodeId: string | null = null;
	let forkCounter = 0;

	const hasAnySelection = Object.keys(argsMap).some(
		k => argsMap[k] != null && String(argsMap[k]) !== 'undefined'
	);

	let pendingBranchEnds: PendingBranchEnd[] = [];

	function connectPrevious(targetId: string): void {
		if (pendingBranchEnds.length) {
			for (const end of pendingBranchEnds) {
				addEdge(elements, end.nodeId, targetId, undefined, end.attrKey, end.attrValue, end.isOnPath, end.isOffPath);
			}
			pendingBranchEnds = [];
		} else if (prevNodeId) {
			addEdge(elements, prevNodeId, targetId, undefined, undefined, undefined, hasAnySelection, false);
		}
	}

	function attrKeysStr(attrs: Record<string, unknown> | undefined): string {
		if (!attrs) return '';
		return Object.keys(attrs).sort().join(',');
	}

	// Strict, via the shared rule. This was the last lenient
	// `String(a) === String(b)` comparison in the editor: DEV-749 made the
	// timeline strict and healed stored values to their declared type, but the
	// graph kept absorbing the drift and could therefore draw a node on-path
	// that the player skips. Applies to chapter nodes too (DEV-833).
	const allAttrsMatch = attributesMatch;

	function allAttrsSelected(attrs: Record<string, unknown>, args: Record<string, string | number | boolean>): boolean {
		return Object.keys(attrs).every(key => key in args && args[key] != null && String(args[key]) !== 'undefined');
	}

	function attrValueKey(attrs: Record<string, unknown>): string {
		return Object.entries(attrs).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => `${k}=${v}`).join('&');
	}

	function attrLabel(attrs: Record<string, unknown>): string {
		return Object.entries(attrs).map(([k, v]) => `${k}=${v}`).join(', ');
	}

	let i = 0;
	while (i < refs.length) {
		const ref = refs[i];
		const refAttrs = ref.attributes;
		const keysStr = attrKeysStr(refAttrs);

		if (refAttrs && keysStr) {
			const group: GraphNodeRef[] = [ref];
			let j = i + 1;
			while (j < refs.length) {
				const nextRef = refs[j];
				if (attrKeysStr(nextRef.attributes) === keysStr) {
					group.push(nextRef);
					j++;
				} else {
					break;
				}
			}

			const valueMap = new Map<string, GraphNodeRef[]>();
			for (const g of group) {
				const valKey = attrValueKey(g.attributes ?? {});
				const bucket = valueMap.get(valKey);
				if (bucket) {
					bucket.push(g);
				} else {
					valueMap.set(valKey, [g]);
				}
			}

			if (valueMap.size <= 1) {
				for (const g of group) {
					const block = allBlocks.find(b => b.slug === g.slug);
					const nodeId = addNode(elements, kind, g, block, activeSlug, undefined, undefined, hasAnySelection, false);
					connectPrevious(nodeId);
					prevNodeId = nodeId;
				}
			} else {
				forkCounter++;
				const forkId = `fork-${forkCounter}`;
				const attrKeys = Object.keys(refAttrs).sort();
				const forkLabel = attrKeys.join(' + ');
				const hasSelection = allAttrsSelected(refAttrs, argsMap);

				const forkClasses = ['fork'];
				if (hasSelection) forkClasses.push('on-path');
				elements.push({
					group: 'nodes',
					data: { id: forkId, label: forkLabel, nodeType: 'fork', attrKey: attrKeys[0] },
					classes: forkClasses.join(' '),
				});

				connectPrevious(forkId);

				const newBranchEnds: PendingBranchEnd[] = [];
				for (const [valKey, groupRefs] of valueMap) {
					const sampleAttrs = groupRefs[0].attributes ?? {};
					const isOnPath = hasSelection && allAttrsMatch(sampleAttrs, argsMap);
					const isOffPath = hasSelection && !isOnPath;
					const branchLabel = attrLabel(sampleAttrs);
					let branchPrevId = forkId;
					let isFirst = true;

					for (const r of groupRefs) {
						const block = allBlocks.find(b => b.slug === r.slug);
						const nodeId = addNode(elements, kind, r, block, activeSlug, attrKeys[0], valKey, isOnPath, isOffPath);
						addEdge(elements, branchPrevId, nodeId, isFirst ? branchLabel : undefined, attrKeys[0], valKey, isOnPath, isOffPath);
						branchPrevId = nodeId;
						isFirst = false;
					}

					newBranchEnds.push({ nodeId: branchPrevId, attrKey: attrKeys[0], attrValue: valKey, isOnPath, isOffPath });
				}

				pendingBranchEnds = newBranchEnds;
				prevNodeId = null;
			}

			i = j;
		} else {
			const block = allBlocks.find(b => b.slug === ref.slug);
			const nodeId = addNode(elements, kind, ref, block, activeSlug, undefined, undefined, hasAnySelection, false);
			connectPrevious(nodeId);
			prevNodeId = nodeId;
			i++;
		}
	}

	return elements;
}

function addNode(
	elements: CyElement[],
	kind: GraphNodeKind,
	ref: GraphNodeRef,
	block: TimelineSceneBlock | undefined,
	activeSlug: string | null,
	attrKey?: string,
	attrValue?: string,
	isOnPath?: boolean,
	isOffPath?: boolean,
): string {
	const id = `${kind}-${ref.slug}`;
	const classes: string[] = [kind];
	if (ref.slug === activeSlug) classes.push('active');
	if (ref.disabled) classes.push('disabled');
	if (isOnPath) classes.push('on-path');
	if (isOffPath) classes.push('off-path');

	const label = kind === 'chapter'
		? buildChapterLabel(ref)
		: buildSceneLabel(ref, block);

	elements.push({
		group: 'nodes',
		data: {
			id,
			label,
			slug: ref.slug,
			nodeType: kind,
			attrKey: attrKey ?? null,
			attrValue: attrValue ?? null,
		},
		classes: classes.join(' '),
	});

	return id;
}

function buildSceneLabel(ref: GraphNodeRef, block: TimelineSceneBlock | undefined): string {
	const duration = block?.durationSeconds ?? 0;
	const durationLabel = duration >= 60
		? `${Math.floor(duration / 60)}m${(duration % 60).toFixed(1)}s`
		: `${duration.toFixed(1)}s`;
	return `${ref.slug}\n${durationLabel}`;
}

function buildChapterLabel(ref: GraphNodeRef): string {
	const count = ref.scenesCount ?? 0;
	const scenesLabel = count === 1 ? '1 scene' : `${count} scenes`;
	return `${ref.slug}\n${scenesLabel}`;
}

function addEdge(
	elements: CyElement[],
	source: string,
	target: string,
	label?: string,
	attrKey?: string,
	attrValue?: string,
	isOnPath?: boolean,
	isOffPath?: boolean,
): void {
	const classes: string[] = [];
	if (isOnPath) classes.push('on-path');
	if (isOffPath) classes.push('off-path');

	elements.push({
		group: 'edges',
		data: {
			id: `e-${source}-${target}`,
			source,
			target,
			label: label ?? '',
			attrKey: attrKey ?? null,
			attrValue: attrValue ?? null,
		},
		classes: classes.length ? classes.join(' ') : undefined,
	});
}
